import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Globe, Activity, ChevronRight, ShieldCheck, TrendingUp, CalendarCheck } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const tooltipStyle = {
  background: "#18181b", border: "1px solid #3f3f46", borderRadius: 0,
  fontSize: 12, color: "#fafafa",
};

function fmtDay(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const floorNice = (n) => {
  const v = Number(n) || 0;
  if (v >= 100) return Math.floor(v / 100) * 100;
  if (v >= 10) return Math.floor(v / 10) * 10;
  return v;
};
const fmtNum = (n) => (Number(n) || 0).toLocaleString("pt-PT");

// Client-facing summary — a positive "resumo das pesquisas" without exposing
// which sites are monitored, their counts or their block/active status.
function ClientSummary({ stats }) {
  const total = stats?.total || 0;
  const today = stats?.new_today || 0;
  const week = stats?.new_week || 0;
  return (
    <div className="bg-[#18181b] border border-[#27272a] p-6 sm:p-8" data-testid="collection-summary">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4 text-[#c8aa6e]" strokeWidth={1.5} />
        <span className="label-tiny">Resumo de hoje</span>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-end gap-6">
        <div className="flex-1">
          <div className="font-head text-3xl sm:text-4xl text-[#fafafa] leading-tight">
            Mais de <span className="text-[#c8aa6e]">{fmtNum(floorNice(total))}</span> perfis verificados hoje
          </div>
          <p className="text-sm text-[#a1a1aa] mt-2 max-w-xl">
            Analisámos os anúncios por si e mantemos tudo atualizado automaticamente todos os dias — sem precisar de saltar entre sites.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 shrink-0">
          <div className="bg-[#09090b] border border-[#27272a] px-4 py-3 min-w-[130px]">
            <div className="flex items-center gap-1.5 label-tiny"><CalendarCheck className="w-3.5 h-3.5" /> Novos hoje</div>
            <div className="font-head text-2xl text-[#fafafa] mt-1">{fmtNum(today)}</div>
          </div>
          <div className="bg-[#09090b] border border-[#27272a] px-4 py-3 min-w-[130px]">
            <div className="flex items-center gap-1.5 label-tiny"><TrendingUp className="w-3.5 h-3.5" /> Novos (7 dias)</div>
            <div className="font-head text-2xl text-[#fafafa] mt-1">{fmtNum(week)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DailyCollection({ sources, stats, isAdmin = false, onSelectSource = () => {} }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/scrape/history", { params: { limit: 14 } }).then(({ data }) => setHistory(data)).catch(() => {});
  }, [isAdmin]);

  // Clients get a friendly summary instead of the operational monitoring panel.
  if (!isAdmin) return <ClientSummary stats={stats} />;

  const chartData = history.map((h) => ({
    label: new Date(h.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }),
    novos: h.new_total,
  }));

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="daily-collection">
      {/* Monitored sites */}
      <div className="bg-[#18181b] border border-[#27272a] lg:col-span-1">
        <div className="px-5 py-3 border-b border-[#27272a] flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#c8aa6e]" strokeWidth={1.5} />
          <span className="label-tiny">Sites Monitorizados</span>
        </div>
        <div className="divide-y divide-[#27272a]">
          {sources.map((s) => {
            const online = !/bloqueado|sem resultados|erro|—/i.test(s.last_status);
            return (
              <button
                type="button"
                key={s.key}
                data-testid={`monitored-${s.key}`}
                onClick={() => onSelectSource(s.key)}
                title={`Ver perfis de ${s.name}`}
                className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[#27272a]/40 transition-colors group"
              >
                <span className="w-1.5 h-8" style={{ background: s.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#fafafa] truncate group-hover:text-[#c8aa6e] transition-colors">{s.name}</div>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-[#10b981]" : "bg-[#ef4444]"}`} />
                    <span className="text-[#71717a] truncate">{online ? "ativo" : "bloqueado"}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-head text-lg text-[#c8aa6e] leading-none">{s.count}</div>
                  {s.last_new > 0 && <div className="text-[10px] text-[#10b981] mt-0.5">+{s.last_new} novos</div>}
                </div>
                <ChevronRight className="w-4 h-4 text-[#3f3f46] group-hover:text-[#c8aa6e] transition-colors shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Daily collection chart */}
      <div className="bg-[#18181b] border border-[#27272a] lg:col-span-2 p-5">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-head text-lg font-normal text-[#fafafa] flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#c8aa6e]" strokeWidth={1.5} /> Recolha Diária
          </h3>
          <span className="label-tiny">Novos perfis por recolha</span>
        </div>
        {chartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-[#71717a]">
            Sem recolhas registadas ainda.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ left: -20, right: 8, top: 8 }}>
              <CartesianGrid stroke="#27272a" vertical={false} />
              <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a55" }} />
              <Bar dataKey="novos" fill="#c8aa6e" radius={0} name="Novos perfis" />
            </BarChart>
          </ResponsiveContainer>
        )}
        {history.length > 0 && (
          <div className="mt-4 text-xs text-[#71717a] flex items-center justify-between border-t border-[#27272a] pt-3">
            <span>Última recolha: {fmtDay(history[history.length - 1]?.date)}</span>
            <span className="text-[#c8aa6e]">+{history[history.length - 1]?.new_total || 0} novos nessa recolha</span>
          </div>
        )}
      </div>
    </div>
  );
}
